import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Pagination, Spinner, Alert, Badge, Row, Col, Container, Form, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import apiService from '../services/apiService';
import Breadcrumb from '../components/Breadcrumb';

// Get variant for status badges
const getBadgeVariant = (status) => {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'danger';
    case 'RUNNING':
      return 'primary';
    case 'PENDING':
      return 'warning';
    default:
      return 'secondary';
  }
};

// Function to format datetime with nanoseconds
const formatDateTimeWithNanos = (dateString) => {
    if (!dateString) return null;
    
    try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
        
        // Extract nanoseconds from the original string if available
        const nanoMatch = dateString.match(/\.(\d{3,9})/);
        let nanos = '000000';
        if (nanoMatch) {
            const fullNanos = nanoMatch[1].padEnd(9, '0');
            nanos = fullNanos.substring(3, 9); // Get microseconds + nanoseconds
        }
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
        return dateString;
    }
};

// Get status icon
const getStatusIcon = (status) => {
  switch (status) {
    case 'COMPLETED':
      return '✓';
    case 'FAILED':
      return '✗';
    case 'RUNNING':
      return '⟳';
    case 'PENDING':
      return '⏳';
    default:
      return '●';
  }
};

// Function to format address from property data
const formatAddress = (propertyData) => {
    if (!propertyData || !Array.isArray(propertyData) || propertyData.length === 0) {
        return 'No address available';
    }
    
    const property = propertyData[0]; // Get first property
    const { address1, address2, city, state, zipcode } = property;
    
    let formattedAddress = '';
    if (address1) formattedAddress += address1;
    if (address2) formattedAddress += `, ${address2}`;
    if (city) formattedAddress += formattedAddress ? `, ${city}` : city;
    if (state) formattedAddress += `, ${state}`;
    if (zipcode) formattedAddress += ` ${zipcode}`;
    
    return formattedAddress || 'Address not available';
};

const Commitments = () => {
    const navigate = useNavigate();
    const [commitments, setCommitments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCommitments, setTotalCommitments] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [showFailedFilter, setShowFailedFilter] = useState(false);
    const [failedFilterValue, setFailedFilterValue] = useState('');
    const pageSize = 10; // Define page size

    // Filter options for status filtering
    const filterOptions = [
        { value: 'all', label: 'All Commitments', description: 'All commitment records' },
        { value: 'completed', label: 'Completed', description: 'Successfully completed commitments' },
        { value: 'failed', label: 'Failed', description: 'Failed commitment records' },
        { value: 'running', label: 'Running', description: 'Currently running commitments' },
        { value: 'pending', label: 'Pending', description: 'Pending commitment records' }
    ];

    // Handle filter change
    const handleFilterChange = (filterValue) => {
        setSelectedFilter(filterValue);
        setCurrentPage(1); // Reset to first page when filter changes
        
        // Show/hide the failed filter based on selection
        if (filterValue === 'failed') {
            setShowFailedFilter(true);
        } else {
            setShowFailedFilter(false);
            setFailedFilterValue(''); // Reset failed filter value
        }
    };

    // Handle failed filter change
    const handleFailedFilterChange = (value) => {
        setFailedFilterValue(value);
        // Only call API on successful filter changes, not on failures
        if (value !== '') {
            setCurrentPage(1);
            fetchCommitments(1);
        }
    };

    // Function to fetch commitments with optional status filter - returns empty list
    const fetchCommitments = useCallback(async (page) => {
        setLoading(true);
        setError(null);
        try {
            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Return empty data
            setCommitments([]);
            setTotalCommitments(0);
            setTotalPages(1);
        } catch (err) {
            setError('Failed to fetch commitments.');
            console.error('Error fetching commitments:', err);
        } finally {
            setLoading(false);
        }
    }, [pageSize, selectedFilter, failedFilterValue]);

    // Handle real-time updates
    const handleCommitmentUpdate = useCallback((updatedCommitment) => {
        setCommitments(prevCommitments => {
            // Check if the updated commitment already exists in our list
            const existingIndex = prevCommitments.findIndex(commitment => commitment.id === updatedCommitment.id);
            
            if (existingIndex >= 0) {
                // Update existing commitment
                const updatedCommitments = [...prevCommitments];
                updatedCommitments[existingIndex] = updatedCommitment;
                return updatedCommitments;
            } else {
                // Add new commitment at the beginning (newest first)
                return [updatedCommitment, ...prevCommitments.slice(0, pageSize - 1)];
            }
        });
    }, [pageSize]);

    // Initial data fetch
    useEffect(() => {
        fetchCommitments(currentPage);
    }, [currentPage, fetchCommitments]);

    // Refetch data when filter changes
    useEffect(() => {
        fetchCommitments(1);
        setCurrentPage(1);
    }, [selectedFilter, fetchCommitments]);

    // Setup WebSocket connection for real-time updates
    useEffect(() => {
        // For now, no WebSocket setup for commitments
        // const cleanupWebSocket = apiService.setupCommitmentsWebSocket(handleCommitmentUpdate);
        
        // Cleanup function
        return () => {
            // if (typeof cleanupWebSocket === 'function') {
            //     cleanupWebSocket();
            // }
        };
    }, []);

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Render professional status badge with enhanced styling
    const renderStatusBadge = (status) => {
        const getStatusConfig = (status) => {
            switch (status) {
                case 'completed':
                    return {
                        bg: 'success',
                        icon: '✓',
                        gradient: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                        shadow: '0 4px 15px rgba(40, 167, 69, 0.3)',
                        pulse: false
                    };
                case 'failed':
                    return {
                        bg: 'danger',
                        icon: '✗',
                        gradient: 'linear-gradient(135deg, #dc3545 0%, #e74c3c 100%)',
                        shadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
                        pulse: false
                    };
                case 'running':
                    return {
                        bg: 'primary',
                        icon: '⟳',
                        gradient: 'linear-gradient(135deg, #007bff 0%, #6610f2 100%)',
                        shadow: '0 4px 15px rgba(0, 123, 255, 0.3)',
                        pulse: true
                    };
                case 'pending':
                    return {
                        bg: 'warning',
                        icon: '⏳',
                        gradient: 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)',
                        shadow: '0 4px 15px rgba(255, 193, 7, 0.3)',
                        pulse: true
                    };
                default:
                    return {
                        bg: 'secondary',
                        icon: '●',
                        gradient: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                        shadow: '0 4px 15px rgba(108, 117, 125, 0.3)',
                        pulse: false
                    };
            }
        };

        const config = getStatusConfig(status);
        
        return (
            <div 
                className={`status-badge-professional ${config.pulse ? 'status-badge-pulse' : ''}`}
                style={{
                    background: config.gradient,
                    boxShadow: config.shadow,
                    color: 'white',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    border: 'none',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    transition: 'all 0.3s ease',
                    cursor: 'default',
                    minWidth: '85px',
                    justifyContent: 'center'
                }}
            >
                <span 
                    className={`status-icon ${config.pulse ? 'status-icon-spin' : ''}`}
                    style={{ 
                        fontSize: '0.9rem',
                        display: 'inline-block'
                    }}
                >
                    {config.icon}
                </span>
                <span>{status}</span>
            </div>
        );
    };

    // Handle row click to navigate to details page
    const handleRowClick = (commitmentId) => {
        navigate(`/commitments/details/${commitmentId}`);
    };

    const breadcrumbItems = [
        { label: 'Dashboard', href: '/', icon: 'fas fa-tachometer-alt' },
        { label: 'Management', href: '#' },
        { label: 'Commitments', href: null }
    ];

    return (
        <Container fluid className="px-4 py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <Breadcrumb items={breadcrumbItems} />
            <style jsx>{`
                .page-title {
                    color: #2c3e50;
                    font-weight: 700;
                    font-size: 2rem;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                
                .page-subtitle {
                    font-size: 1rem;
                    color: #6c757d;
                }
                
                .stats-summary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    color: white;
                    margin-bottom: 1.5rem;
                    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
                }
                
                .automation-runs-container {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 20px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                    color: white;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                }
                
                .stats-card {
                    background: rgba(255,255,255,0.1);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255,255,255,0.2);
                    border-radius: 15px;
                    padding: 1.5rem;
                    color: white;
                    transition: transform 0.3s ease;
                }
                
                .stats-card:hover {
                    transform: translateY(-5px);
                }
                
                .main-card {
                    border: none;
                    border-radius: 20px;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.1);
                    overflow: hidden;
                }
                
                .card-header-custom {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    padding: 1.5rem 2rem;
                }
                
                .table-modern {
                    width: 100%;
                    margin-bottom: 0;
                    table-layout: auto;
                }
                
                .table-responsive {
                    overflow-x: auto !important;
                    overflow-y: visible;
                }
                
                .table-modern thead th {
                    background-color: #f8f9fa;
                    border: none;
                    font-weight: 600;
                    color: #495057;
                    padding: 12px 15px;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    white-space: nowrap;
                    border-bottom: 2px solid #e9ecef;
                }
                
                .table-modern tbody td {
                    padding: 15px;
                    border: none;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f3f4;
                    font-size: 0.85rem;
                }
                
                .table-modern tbody tr:hover {
                    background-color: #f8f9fa;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                    transition: all 0.2s ease;
                }
                
                .id-badge {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 0.25rem 0.5rem;
                    border-radius: 6px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                
                .duration-badge {
                    background: #e9ecef;
                    color: #495057;
                    padding: 0.25rem 0.5rem;
                    border-radius: 6px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                
                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 4rem 2rem;
                    text-align: center;
                }
                
                .loading-spinner {
                    width: 3rem;
                    height: 3rem;
                }
                
                .empty-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 4rem 2rem;
                    text-align: center;
                    color: #6c757d;
                }
                
                .empty-state-icon {
                    font-size: 4rem;
                    margin-bottom: 1rem;
                    opacity: 0.5;
                }
                
                .pagination-container {
                    padding: 1.5rem 2rem;
                    border-top: 1px solid #e9ecef;
                    background-color: #f8f9fa;
                }
                
                .pagination-info {
                    color: #6c757d;
                    font-size: 0.875rem;
                }
                
                .status-badge-pulse {
                    animation: pulse 2s infinite;
                }
                
                .status-icon-spin {
                    animation: spin 2s linear infinite;
                }
                
                @keyframes pulse {
                    0% {
                        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7);
                    }
                    70% {
                        box-shadow: 0 0 0 10px rgba(255, 193, 7, 0);
                    }
                    100% {
                        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
                    }
                }
                
                @keyframes spin {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
                
                .name-cell {
                    max-width: 150px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                
                .address-cell {
                    max-width: 200px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                
                .error-cell {
                    max-width: 200px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                
                .date-cell {
                    min-width: 120px;
                }
                
                .status-cell {
                    min-width: 100px;
                }
                
                @media (max-width: 768px) {
                    .table-responsive {
                        font-size: 0.75rem;
                    }
                    
                    .name-cell {
                        max-width: 100px;
                    }
                    
                    .address-cell {
                        max-width: 120px;
                    }
                    
                    .error-cell {
                        max-width: 100px;
                    }
                }
            `}</style>

            {/* Page Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="page-title mb-2">
                        Commitments
                    </h1>
                    <p className="page-subtitle mb-0">Monitor and track your commitment records</p>
                </div>
                <Button 
                    variant={showFilters ? "primary" : "outline-primary"}
                    onClick={() => setShowFilters(!showFilters)}
                    className="d-flex align-items-center gap-2"
                >
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                    <i className="fas fa-filter"></i>
                </Button>
            </div>

            {/* Enhanced Professional Filter Section */}
            {showFilters && (
                <Card 
                    className="mb-4 filter-card" 
                    style={{ 
                        border: 'none', 
                        boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                        animation: 'slideDown 0.3s ease-out'
                    }}
                >
                    <Card.Header 
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '16px 16px 0 0',
                            padding: '1rem 1.5rem'
                        }}
                    >
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                                <i className="fas fa-sliders-h me-2"></i>
                                <h6 className="mb-0 fw-bold">Filters</h6>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button 
                                    className="btn btn-sm btn-outline-light"
                                    onClick={() => setShowFilters(false)}
                                    style={{ borderRadius: '6px', padding: '0.25rem 0.5rem' }}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body style={{ padding: '1rem 1rem 2rem 1rem !important' }}>
                        <Row className="align-items-center g-4 mb-2">
                            <Col lg={3}>
                                <div className="filter-controls">
                                    <label className="form-label fw-semibold text-muted mb-2">
                                        Status Filter
                                    </label>
                                    <div className="position-relative">
                                        <Form.Select
                                            value={selectedFilter}
                                            onChange={(e) => handleFilterChange(e.target.value)}
                                            className="form-select-modern"
                                            style={{
                                                border: '2px solid #e9ecef',
                                                borderRadius: '12px',
                                                fontSize: '0.95rem',
                                                padding: '0.75rem 1rem',
                                                background: 'white',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#667eea';
                                                e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#e9ecef';
                                                e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                                            }}
                                        >
                                            {filterOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <i className="fas fa-chevron-down position-absolute" 
                                           style={{
                                               right: '1rem',
                                               top: '50%',
                                               transform: 'translateY(-50%)',
                                               pointerEvents: 'none',
                                               color: '#6c757d',
                                               fontSize: '0.8rem'
                                           }}>
                                        </i>
                                    </div>
                                </div>
                            </Col>
                            
                            {showFailedFilter && (
                                <Col lg={3}>
                                    <div className="filter-controls">
                                        <label className="form-label fw-semibold text-muted mb-2">
                                            Failed Commitment
                                        </label>
                                        <div className="position-relative">
                                            <Form.Select
                                                value={failedFilterValue}
                                                onChange={(e) => handleFailedFilterChange(e.target.value)}
                                                className="form-select-modern"
                                                style={{
                                                    border: '2px solid #e9ecef',
                                                    borderRadius: '12px',
                                                    fontSize: '0.95rem',
                                                    padding: '0.75rem 1rem',
                                                    background: 'white',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <option value="">Select Failed Commitment</option>
                                                <option value="type_a">Type A Commitments</option>
                                                <option value="type_b">Type B Commitments</option>
                                                <option value="type_c">Type C Commitments</option>
                                            </Form.Select>
                                        </div>
                                    </div>
                                </Col>
                            )}
                            
                        </Row>
                        
                        {/* Filter Statistics */}
                        <Row className="mt-0 pt-3 pb-2" style={{ borderTop: '1px solid #e9ecef', minHeight: '60px' }}>
                            <Col>
                                <div className="d-flex align-items-center justify-content-end flex-wrap gap-3 h-100">
                                    
                                    <div className="filter-actions d-flex align-items-center justify-content-center" style={{ height: '100%' }}>
                                        <button 
                                            className="btn btn-outline-primary btn-sm me-2"
                                            onClick={() => {
                                                setSelectedFilter('all');
                                                setCurrentPage(1);
                                            }}
                                            style={{ 
                                                borderRadius: '8px',
                                                height: '36px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <i className="fas fa-refresh me-1"></i>
                                            Reset
                                        </button>
                                        <button 
                                            className="btn btn-primary btn-sm"
                                            onClick={() => fetchCommitments(1)}
                                            style={{ 
                                                borderRadius: '8px',
                                                height: '36px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <i className="fas fa-sync-alt me-1"></i>
                                            Refresh
                                        </button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Statistics Summary Cards */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="border-0 shadow-sm h-100" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <div className="text-white-50 small text-uppercase fw-bold mb-1">
                                    Total Records
                                </div>
                                <div className="h4 mb-0 fw-bold">
                                    {totalCommitments}
                                </div>
                            </div>
                            <div className="ms-3">
                                <i className="fas fa-handshake fa-2x opacity-75"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Main Content Card */}
            <Card className="main-card">
                <Card.Header className="card-header-custom">
                    <Row className="align-items-center">
                        <Col>
                            <h5 className="mb-0 fw-bold">{filterOptions.find(opt => opt.value === selectedFilter)?.label}</h5>
                        </Col>
                        
                    </Row>
                </Card.Header>
                
                <Card.Body className="p-0">
                    {loading && (
                        <div className="loading-container">
                            <Spinner 
                                animation="border" 
                                variant="primary" 
                                className="loading-spinner mb-3"
                            />
                            <h5 className="text-muted">Loading commitments...</h5>
                            <p className="text-muted mb-0">Please wait while we fetch the latest data</p>
                        </div>
                    )}
                    
                    {error && (
                        <div className="p-4">
                            <Alert variant="danger" className="border-0 rounded-3">
                                <Alert.Heading className="h6">⚠️ Error Loading Data</Alert.Heading>
                                {error}
                            </Alert>
                        </div>
                    )}
                    
                    {!loading && !error && (
                        <>
                            {commitments.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover className="table-modern mb-0">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Status</th>
                                                <th>Created</th>
                                                <th>Updated</th>
                                                <th>Priority</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {commitments.map((commitment) => (
                                                <tr 
                                                    key={commitment.id} 
                                                    onClick={() => handleRowClick(commitment.id)}
                                                    style={{cursor: 'pointer'}}
                                                >
                                                    <td>
                                                        <span className="id-badge">#{commitment.id}</span>
                                                    </td>
                                                    <td className="name-cell" title={commitment.name}>
                                                        {commitment?.name?.length > 12 ? `${commitment.name.substring(0, 12)}...` : commitment.name}
                                                    </td>
                                                    <td className="address-cell" title={commitment.description}>
                                                        {commitment.description}
                                                    </td>
                                                    <td className="status-cell">{renderStatusBadge(commitment.status)}</td>
                                                    <td className="date-cell" title={formatDateTimeWithNanos(commitment.created_at)}>
                                                        <div style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                                                            {formatDateTimeWithNanos(commitment.created_at)}
                                                        </div>
                                                    </td>
                                                    <td className="date-cell" title={commitment.updated_at ? formatDateTimeWithNanos(commitment.updated_at) : 'Not Updated'}>
                                                        {commitment.updated_at ? (
                                                            <div style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                                                                {formatDateTimeWithNanos(commitment.updated_at)}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {commitment.priority ? (
                                                            <span className="duration-badge">
                                                                {commitment.priority}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-state-icon">🤝</div>
                                    <h5 className="mb-2">
                                        {selectedFilter === 'all' ? 'No Commitments Found' : `No ${filterOptions.find(opt => opt.value === selectedFilter)?.label} Found`}
                                    </h5>
                                    <p className="mb-0">
                                        {selectedFilter === 'all' 
                                            ? 'Create your first commitment to see results here'
                                            : `No commitments match the current filter criteria. Try adjusting your filters or check back later.`
                                        }
                                    </p>
                                    {selectedFilter !== 'all' && (
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm" 
                                            className="mt-3"
                                            onClick={() => handleFilterChange('all')}
                                        >
                                            Show All Commitments
                                        </Button>
                                    )}
                                </div>
                            )}
                            
                            {commitments.length > 0 && (
                                <div className="pagination-container">
                                    <Row className="align-items-center">
                                        <Col>
                                            <div className="pagination-info">
                                                {/* Showing <strong>{commitments.length}</strong> of <strong>{totalCommitments}</strong> entries */}
                                            </div>
                                        </Col>
                                        <Col xs="auto">
                                            <Pagination className="mb-0">
                                                <Pagination.First
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={currentPage === 1}
                                                />
                                                <Pagination.Prev 
                                                    onClick={() => handlePageChange(currentPage - 1)} 
                                                    disabled={currentPage === 1}
                                                />
                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    const pageNumber = Math.max(1, currentPage - 2) + i;
                                                    if (pageNumber <= totalPages) {
                                                        return (
                                                            <Pagination.Item
                                                                key={pageNumber}
                                                                active={pageNumber === currentPage}
                                                                onClick={() => handlePageChange(pageNumber)}
                                                            >
                                                                {pageNumber}
                                                            </Pagination.Item>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                                <Pagination.Next 
                                                    onClick={() => handlePageChange(currentPage + 1)} 
                                                    disabled={currentPage === totalPages}
                                                />
                                                <Pagination.Last
                                                    onClick={() => handlePageChange(totalPages)}
                                                    disabled={currentPage === totalPages}
                                                />
                                            </Pagination>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default Commitments;